import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCatalog } from '../context/CatalogContext';
import MeasurableField from '../components/MeasurableField';
import axios from 'axios';
import ConfirmModal from '../components/ConfirmModal';
import VerifiedFlame from '../components/VerifiedFlame';
import PowerScoreCard from '../components/PowerScoreCard';
import MentalSkillsCard from '../components/MentalSkillsCard';
import MentalToughnessCard from '../components/MentalToughnessCard';
import { AthleteProfileShell } from '../components/ProfileShell';
import AccoladesEditor from '../components/athleteEditor/AccoladesEditor';
import CredentialsEditor from '../components/athleteEditor/CredentialsEditor';
import { POWER_SCORE_REQUIRED } from '../lib/powerScoreFields';

// Required for the Power Score to compute. Mirrors REQUIRED_FOR_SCORE in
// backend/app/services/power_score.py — keep these in sync.



export default function PlayerStatsPage() {
  const { user, API_URL } = useAuth();
  const catalog = useCatalog();
  const [sportName, setSportName] = useState('Football');
  const [measurables, setMeasurables] = useState({});   // { key: value }
  const [gear, setGear] = useState({
    shoe_size: '', shoe_vendor: '', cleat_vendor: '', jersey_size: '',
    hand_preference: '', foot_preference: '',
  });

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const [verified, setVerified] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [powerScore, setPowerScore] = useState(null);
  const [smtqSummary, setSmtqSummary] = useState(null);

  // Power Score is FREE (spec section 2): it is proof the athlete is real, not
  // leverage sold back to them. Do not reintroduce a role-derived lock here —
  // `base_user` on the wire means "no subscription row", NOT "not entitled".

  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    }
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/athletes/profile`, getAxiosConfig());
      if (response.data) {
        const d = response.data;
        setSportName(d.sport || 'Football');
        const vals = {};
        const ver = {};
        (d.measurables || []).forEach(m => { vals[m.key] = m.value ?? ''; ver[m.key] = !!m.verified; });
        ['shoe_size', 'shoe_vendor', 'cleat_vendor', 'jersey_size', 'hand_preference', 'foot_preference']
          .forEach(k => { ver[k] = !!d[`${k}_verified`]; });
        setMeasurables(vals);
        setVerified(ver);
        setGear({
          shoe_size: d.shoe_size || '', shoe_vendor: d.shoe_vendor || '',
          cleat_vendor: d.cleat_vendor || '', jersey_size: d.jersey_size || '',
          hand_preference: d.hand_preference || '', foot_preference: d.foot_preference || '',
        });
        setPowerScore(d.power_score || null);
        setSmtqSummary(d.smtq_summary || null);
      }
    } catch (error) {
      console.log('Error fetching stats:', error);
    }
  };

  const missingForPowerScore = POWER_SCORE_REQUIRED
    .filter(f => !measurables[f.name])
    .map(f => f.label);

  // A 422 carries a message the athlete can act on ("dash_40 must be a number");
  // anything else (a 500's "Internal server error" included) stays generic.
  const saveErrorMessage = (e) => {
    const detail = e?.response?.data?.detail;
    return e?.response?.status === 422 && typeof detail === 'string' && detail.trim()
      ? detail
      : 'Error saving — please try again';
  };

  const saveMeasurable = async (key, value) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, { measurables: { [key]: value } }, getAxiosConfig());
      setMessageType(''); setMessage('Saved'); setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessageType('error'); setMessage(saveErrorMessage(e)); setTimeout(() => setMessage(''), 4000);
    }
  };

  const saveFlat = async (name, value) => {
    try {
      await axios.post(`${API_URL}/athletes/profile`, { [name]: value }, getAxiosConfig());
      setMessageType(''); setMessage('Saved'); setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessageType('error'); setMessage(saveErrorMessage(e)); setTimeout(() => setMessage(''), 4000);
    }
  };

  // For MeasurableField: update local state on keystroke...
  const onMeasurableChange = (key, value) => setMeasurables(prev => ({ ...prev, [key]: value }));

  // ...and commit (save) on blur/select, with the unverify-on-change confirm.
  const onMeasurableCommit = (key, value) => {
    if (verified[key]) {
      setConfirmAction({
        title: 'Unverify Stat',
        message: 'This stat is currently verified. Changing it will mark it as unverified and you will not be able to return to the verified stat unless verified by ProZpects staff or Partner.',
        confirmLabel: 'Continue',
        danger: true,
        onConfirm: async () => { setConfirmAction(null); setVerified(prev => ({ ...prev, [key]: false })); await saveMeasurable(key, value); },
        onCancel: () => { setConfirmAction(null); fetchStats(); },
      });
      return;
    }
    saveMeasurable(key, value);
  };

  const handleGearChange = (e) => setGear(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleGearCommit = (e) => {
    const { name, value } = e.target;
    if (verified[name]) {
      setConfirmAction({
        title: 'Unverify Stat',
        message: 'This stat is currently verified. Changing it will mark it as unverified and you will not be able to return to the verified stat unless verified by ProZpects staff or Partner.',
        confirmLabel: 'Continue', danger: true,
        onConfirm: async () => { setConfirmAction(null); setVerified(prev => ({ ...prev, [name]: false })); await saveFlat(name, value); },
        onCancel: () => { setConfirmAction(null); fetchStats(); },
      });
      return;
    }
    saveFlat(name, value);
  };

  const VENDOR_OPTIONS = ['Nike', 'Adidas', 'New Balance', 'Under Armour', 'Jordan', 'Mizuno', 'Asics', 'Hoka', 'Brooks', 'Other'];
  const JERSEY_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  const HAND_PREF_OPTIONS = ['Right', 'Left', 'Ambidextrous'];
  const FOOT_PREF_OPTIONS = ['Right', 'Left', 'Both'];
  const sizeSelectFields = [
    { name: 'shoe_vendor', label: 'Shoe Vendor', options: VENDOR_OPTIONS },
    { name: 'cleat_vendor', label: 'Cleat Vendor', options: VENDOR_OPTIONS },
    { name: 'jersey_size', label: 'Jersey Size', options: JERSEY_OPTIONS },
    { name: 'hand_preference', label: 'Hand Preference', options: HAND_PREF_OPTIONS },
    { name: 'foot_preference', label: 'Foot Preference', options: FOOT_PREF_OPTIONS },
  ];

  const sportSlug = (catalog.sports.find(s => s.name === sportName) || {}).slug || 'football';
  const sportMeta = catalog.getSport(sportSlug);
  const scoreSupported = sportMeta ? sportMeta.score_supported : false;

  return (
    <AthleteProfileShell activeTab="Measurables">
      {message && (
        <div className={`settings-toast ${messageType === 'error' ? 'settings-toast--error' : ''}`} role="alert">
          {message}
        </div>
      )}

      {scoreSupported ? (
        <div style={{ marginBottom: 16 }}>
          <PowerScoreCard score={powerScore} missing={missingForPowerScore} />
        </div>
      ) : (
        <div className="settings-section" style={{ marginBottom: 16 }}>
          <p className="settings-section__desc">Power Score coming soon for {sportName}.</p>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <MentalSkillsCard userId={user?.id} />
      </div>

      <div className="ed-card" style={{ marginBottom: 16 }}>
        <MentalToughnessCard
          summary={smtqSummary}
          testId="measurables_mental_toughness"
          emptyText="Take the Mental Toughness test in the ProZpects app."
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontSize: '13px', color: '#64748b' }}>
        <VerifiedFlame />
        {/* Scoped to measurables: the Mental Toughness card above uses the same
            glyph for a clean self-report ("Verified self-report"), not staff validation. */}
        <span>Next to a measurable: validated by ProZpects or ProZpects Partner</span>
      </div>

      {catalog.groupedMeasurablesFor(sportSlug).map(group => (
        <div className="settings-section" key={group.category}>
          <h2 className="settings-section__title">{group.category}</h2>
          {(() => {
            const rows = [];
            for (let i = 0; i < group.measurables.length; i += 2) {
              const pair = group.measurables.slice(i, i + 2);
              rows.push(
                <div className="form-row" key={i}>
                  {pair.map(m => (
                    <MeasurableField
                      key={m.key}
                      measurable={m}
                      value={measurables[m.key] ?? ''}
                      verified={!!verified[m.key]}
                      onValueChange={onMeasurableChange}
                      onCommit={onMeasurableCommit}
                    />
                  ))}
                </div>
              );
            }
            return rows;
          })()}
        </div>
      ))}

      <div className="settings-section">
        <h2 className="settings-section__title">Gear &amp; Preferences</h2>
        <div className="form-row">
          <div className="form-field">
            <label className="form-field__label" htmlFor="shoe_size">
              Shoe Size (US){verified.shoe_size && <VerifiedFlame />}
            </label>
            <input
              id="shoe_size" name="shoe_size" type="number" step="0.5"
              className="form-field__input"
              value={gear.shoe_size}
              onChange={handleGearChange}
              onBlur={handleGearCommit}
            />
          </div>
        </div>
        {(() => {
          const rows = [];
          for (let i = 0; i < sizeSelectFields.length; i += 2) {
            const pair = sizeSelectFields.slice(i, i + 2);
            rows.push(
              <div className="form-row" key={`gear-${i}`}>
                {pair.map(field => (
                  <div className="form-field" key={field.name}>
                    <label className="form-field__label" htmlFor={field.name}>
                      {field.label}{verified[field.name] && <VerifiedFlame />}
                    </label>
                    <select
                      id={field.name} name={field.name}
                      className="form-field__input"
                      value={gear[field.name] || ''}
                      onChange={handleGearChange}
                      onBlur={handleGearCommit}
                    >
                      <option value="">Select...</option>
                      {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            );
          }
          return rows;
        })()}
      </div>

      {/* Accolades — shared with the admin edit panel (AccoladesEditor). */}
      <AccoladesEditor sportName={sportName} />

      {/* Credentials — equestrian-only membership / safety / eligibility / rating facts */}
      {sportSlug === 'equestrian' && <CredentialsEditor />}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          danger={confirmAction.danger}
          onConfirm={confirmAction.onConfirm}
          onCancel={confirmAction.onCancel || (() => setConfirmAction(null))}
        />
      )}
    </AthleteProfileShell>
  );
}
